import moment from "moment";
import React, { useState } from "react";
import { AiFillDelete } from "react-icons/ai";
import { FiEye } from "react-icons/fi";
import { MdEdit } from "react-icons/md";

const searchContainerStyles = {
  position: "relative",
  width: "100%",
  maxWidth: "420px",
};

const searchInputStyles = {
  width: "100%",
  padding: "12px 44px 12px 44px",
  fontSize: "14px",
  fontWeight: 400,
  color: "var(--ink)",
  backgroundColor: "var(--land-soft)",
  border: "1.5px solid var(--line-strong)",
  borderRadius: "999px",
  outline: "none",
  transition: "all 0.25s ease",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const searchInputFocusStyles = {
  ...searchInputStyles,
  borderColor: "var(--brand)",
  backgroundColor: "#fff",
  boxShadow: "0 0 0 4px var(--brand-ring)",
};

const searchIconStyles = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--ink-3)",
  pointerEvents: "none",
  transition: "color 0.25s ease",
};

const clearBtnStyles = {
  position: "absolute",
  right: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--ink-3)",
  transition: "all 0.2s ease",
};

const SearchIcon = ({ color = "var(--ink-3)" }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ stroke: color }}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HighlightText = ({ text = "", highlight = "" }) => {
  if (!highlight.trim()) return <>{text}</>;

  const regex = new RegExp(
    `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: "var(--brand-tint-2)",
              color: "var(--brand-hover)",
              padding: "1px 2px",
              borderRadius: "3px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

const PolygonListTable = ({
  polygons = [],
  onDelete = () => {},
  onEdit = () => {},
  onAdd = () => {},
  onView = () => {},
  pagination = { page: 1, limit: 10, totalPages: 1, total: 0 },
  search = "",
  onPageChange = () => {},
  onSearchChange = () => {}
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const { totalPages, page: currentPage } = pagination;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }
      if (currentPage >= totalPages - 2) {
        start = Math.max(totalPages - 3, 2);
      }

      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const paginationBtnStyle = (isActive) => ({
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: isActive ? 600 : 400,
    color: isActive ? "#fff" : "var(--ink-2)",
    backgroundColor: isActive ? "var(--brand)" : "transparent",
    border: isActive ? "1px solid var(--brand)" : "1px solid var(--line)",
    borderRadius: "999px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "36px",
  });

  const navBtnStyle = (disabled) => ({
    padding: "6px 14px",
    fontSize: "13px",
    fontWeight: 500,
    color: disabled ? "var(--line-strong)" : "var(--ink-2)",
    backgroundColor: "transparent",
    border: "1px solid var(--line)",
    borderRadius: "999px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
  });

  return (
    <div className="">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <p className="page-title mb-0" style={{ margin: 0 }}>
          Polygon List
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "nowrap" }}>
          <div style={{ ...searchContainerStyles, minWidth: "200px" }}>
            <div style={searchIconStyles}>
              <SearchIcon color={isFocused ? "var(--brand)" : "var(--ink-3)"} />
            </div>
            <input
              id="polygon-search-input"
              type="text"
              placeholder="Search by polygon name…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              style={isFocused ? searchInputFocusStyles : searchInputStyles}
            />
            {search && (
              <button
                onClick={() => onSearchChange("")}
                style={clearBtnStyles}
                title="Clear search"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "var(--brand)";
                  e.currentTarget.style.backgroundColor = "var(--brand-tint)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "var(--ink-3)";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <ClearIcon />
              </button>
            )}
          </div>
          <button className="btn fribe-primary-btn" onClick={onAdd}>
            Add
          </button>
        </div>
      </div>
      {polygons.length ? (
        <>
          <table className="table">
            <thead>
            <tr>
              <th className="user-header-item">Name</th>
              <th className="user-header-item">Name2</th>
              <th className="user-header-item">Formatted Address</th>
              <th className="user-header-item">API Channel</th>
              <th className="user-header-item header-item-center">createdAt</th>
              <th className="user-header-item header-item-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {polygons.map((item) => {
              const {
                _id: id,
                name,
                name2,
                formattedAddress,
                createdAt,
                location,
                pivotPoint,
                type,
                apiChannel,
                enableCustomAddress,
                customAddresses,
              } = item ?? {};
              return (
                <tr className="data-row-verticle-middle" key={id}>
                  <td className="user-data-item data-item-captilize text-nowrap">
                    <HighlightText text={name} highlight={search} />
                  </td>
                  <td className="user-data-item data-item-captilize text-nowrap">
                    {name2}
                  </td>
                  <td className="user-data-item">{formattedAddress}</td>
                  <td className="user-data-item">{apiChannel}</td>
                  <td className="user-data-item data-item-center">
                    {moment(createdAt).format("MMM D, YYYY [at] HH:MM A")}
                  </td>
                  <td className="user-data-item data-item-center ">
                    <button
                      className="btn"
                      onClick={() => {
                        const data = {
                          name,
                          name2,
                          formattedAddress,
                          location,
                          type,
                          pivotPoint,
                          apiChannel,
                          enableCustomAddress,
                          customAddresses,
                        };
                        onView(id, data);
                      }}
                    >
                      <FiEye />
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        const data = {
                          name,
                          name2,
                          formattedAddress,
                          location,
                          type,
                          pivotPoint,
                          apiChannel,
                          enableCustomAddress,
                          customAddresses,
                        };
                        onEdit(id, data);
                      }}
                    >
                      <MdEdit />
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        onDelete(id);
                      }}
                    >
                      <AiFillDelete />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
          {/* ── Pagination ── */}
          {pagination.total > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px",
                marginTop: "16px",
                padding: "12px 0",
              }}
            >
              <span style={{ fontSize: "13px", color: "var(--ink-3)" }}>
                Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} polygons
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page === 1}
                  style={navBtnStyle(pagination.page === 1)}
                >
                  Previous
                </button>
                {getPageNumbers().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} style={{ padding: "0 4px", color: "var(--ink-3)" }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => onPageChange(p)}
                      style={paginationBtnStyle(p === pagination.page)}
                      onMouseEnter={(e) => {
                        if (p !== pagination.page) {
                          e.currentTarget.style.backgroundColor = "var(--land)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (p !== pagination.page) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => onPageChange(Math.min(pagination.totalPages, pagination.page + 1))}
                  disabled={pagination.page === pagination.totalPages}
                  style={navBtnStyle(pagination.page === pagination.totalPages)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div>No matching result found</div>
      )}
    </div>
  );
};

export default PolygonListTable;
